import React, { useCallback } from 'react';
import { type SubmitHandler } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import {
  checkGuaranteesApplicationRequest,
  fetchGuaranteesAccounts,
  fetchGuaranteesBaseRelations,
  fetchGuaranteesBeneficiary,
  fetchGuaranteesForm,
  fetchGuaranteesPrincipalDebitAccounts,
  fetchGuaranteesPrincipal,
  fetchGuaranteesProzorroTenderById,
  fetchGuaranteesSecurityCurrencies,
  patchGuaranteesApplicationMainDeal,
  patchGuaranteesApplicationSecret,
  patchGuaranteesApplicationTender,
  patchGuaranteesBaseRelations,
  patchGuaranteesBeneficiary,
  patchGuaranteesForm,
  patchGuaranteesPrincipal,
  patchGuaranteesRootApplicationSection,
  putGuaranteesSecurityCurrencies,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import type {
  TGuaranteesApplication,
  TGuaranteesProzorroTender,
  TPatchGuaranteesBaseRelationsPayload,
  TPatchGuaranteesBeneficiaryPayload,
  TPatchGuaranteesFormPayload,
  TPatchGuaranteesPrincipalPayload,
} from '~/store/guarantees/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useGuaranteesApplicationRequest } from '../../hooks/useGuaranteesApplicationRequest.ts';
import {
  buildGuaranteesRootSectionPayload,
  buildGuaranteesSecurityCurrenciesPayload,
  buildGuaranteesSecretSectionPayload,
  isGuaranteesApplicationSelectionPatchSuccessful,
} from '../../rootSection/utils.ts';
import type {
  TGuaranteesContractOption,
  TGuaranteesRootSectionFormValues,
  TGuaranteesSecretSectionSaveContext,
} from '../../rootSection/types.ts';
import type { TGuaranteesStoredAccountSelection } from '../../rootSection/utils.ts';
import type { TGuaranteesModalAlertState, TGuaranteesModalSuccessState } from '../../types.ts';
import {
  buildApplicationSecurityCheckState,
  getGuaranteesSecretValidationError,
} from '../utils/applicationSubmit.ts';

export type TUseApplicationRequestActionsParams = {
  applicationId: string;
  contragentId: string;
  currentApplicationVersion: number;
  currentRouteApplication: TGuaranteesApplication | null;
  formDefaultValues: TGuaranteesRootSectionFormValues;
  getValues: () => TGuaranteesRootSectionFormValues;
  handleSubmit: (
    handler: SubmitHandler<TGuaranteesRootSectionFormValues>
  ) => React.FormEventHandler<HTMLFormElement>;
  navigateBackToApplications: () => void;
  onCheckApplicationSuccess?: (application: TGuaranteesApplication) => void;
  onSecretSectionSaveSuccess?: () => Promise<void>;
  prozorroTender: TGuaranteesProzorroTender | null;
  runGuaranteesApplicationRequest: ReturnType<typeof useGuaranteesApplicationRequest>;
  setIsSecretSavedForCurrentApplication: (value: boolean) => void;
  setModalAlert: React.Dispatch<React.SetStateAction<TGuaranteesModalAlertState>>;
  setModalSuccess: React.Dispatch<React.SetStateAction<TGuaranteesModalSuccessState>>;
  shouldSkipNextAccountRestrictionCheckRef: React.MutableRefObject<boolean>;
  showGuaranteesRequestError: (error: unknown) => void;
  storedAccountSelection: TGuaranteesStoredAccountSelection | null;
};

export const useApplicationRequestActions = ({
  applicationId,
  contragentId,
  currentApplicationVersion,
  currentRouteApplication,
  formDefaultValues,
  getValues,
  handleSubmit,
  navigateBackToApplications,
  onCheckApplicationSuccess,
  onSecretSectionSaveSuccess,
  prozorroTender,
  runGuaranteesApplicationRequest,
  setIsSecretSavedForCurrentApplication,
  setModalAlert,
  setModalSuccess,
  shouldSkipNextAccountRestrictionCheckRef,
  showGuaranteesRequestError,
  storedAccountSelection,
}: TUseApplicationRequestActionsParams) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleSaveRootSection = async (
    values: TGuaranteesRootSectionFormValues
  ): Promise<boolean> => {
    const result = await runGuaranteesApplicationRequest({
      request: (params) =>
        dispatch(
          patchGuaranteesRootApplicationSection({
            ...params,
            payload: buildGuaranteesRootSectionPayload(values),
          })
        ).unwrap(),
    });

    return !!result;
  };

  const handleRootFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<boolean> => {
    let isSaved = false;

    await handleSubmit(async (values) => {
      isSaved = await handleSaveRootSection(values);
    })(event);

    return isSaved;
  };

  const handleCheckApplicationRequest = async () => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    const values = getValues();
    const { hasSecurityDraftChanges, selectedSecurityType, shouldValidateSecretBeforeCheck } =
      buildApplicationSecurityCheckState({
        application: currentRouteApplication,
        defaultValues: formDefaultValues,
        storedAccountSelection,
        values,
      });

    if (shouldValidateSecretBeforeCheck) {
      const firstError = getGuaranteesSecretValidationError(values);

      if (firstError) {
        setModalAlert({ text: firstError });

        return;
      }
    }

    let checkVersion = currentApplicationVersion;

    if (selectedSecurityType && hasSecurityDraftChanges) {
      try {
        const secretApplication = await dispatch(
          patchGuaranteesApplicationSecret({
            contragentId,
            applicationId,
            version: currentApplicationVersion,
            payload: buildGuaranteesSecretSectionPayload(values),
          })
        ).unwrap();

        const secretVersion =
          (secretApplication as TGuaranteesApplication).version ?? currentApplicationVersion;
        const securityCurrenciesApplication = await dispatch(
          putGuaranteesSecurityCurrencies({
            contragentId,
            applicationId,
            version: secretVersion,
            payload: buildGuaranteesSecurityCurrenciesPayload(values),
          })
        ).unwrap();

        checkVersion =
          (securityCurrenciesApplication as TGuaranteesApplication).version ?? secretVersion;
      } catch (error) {
        showGuaranteesRequestError(error);

        return;
      }
    }

    const checkAccountRestriction = !shouldSkipNextAccountRestrictionCheckRef.current;

    try {
      const checkedApplication = await dispatch(
        checkGuaranteesApplicationRequest({
          contragentId,
          applicationId,
          version: checkVersion,
          checkAccountRestriction,
        })
      ).unwrap();

      shouldSkipNextAccountRestrictionCheckRef.current = false;

      if (onCheckApplicationSuccess) {
        onCheckApplicationSuccess(checkedApplication);

        return;
      }

      setModalSuccess({
        text: guaranteesClientText.applicationSubmitSuccessMessage,
        buttonValue: guaranteesClientText.applicationSubmitSuccessButton,
        onClick: navigateBackToApplications,
        onClose: navigateBackToApplications,
      });
    } catch (error) {
      if (checkAccountRestriction) {
        shouldSkipNextAccountRestrictionCheckRef.current = true;
      }

      showGuaranteesRequestError(error);
    }
  };

  const handleLoadAccounts = useCallback(async ({ isPrincipalDebitAccount }: {
    isPrincipalDebitAccount: boolean;
  }) => {
    if (!contragentId) {
      return;
    }

    await runGuaranteesApplicationRequest({
      request: () =>
        dispatch(fetchGuaranteesAccounts({ contragentId, isPrincipalDebitAccount })).unwrap(),
    });
  }, [contragentId, dispatch, runGuaranteesApplicationRequest]);

  const handleLoadPrincipalDebitAccounts = useCallback(async () => {
    if (!contragentId) {
      return;
    }

    await runGuaranteesApplicationRequest({
      request: () =>
        dispatch(fetchGuaranteesPrincipalDebitAccounts({ contragentId })).unwrap(),
    });
  }, [contragentId, dispatch, runGuaranteesApplicationRequest]);

  const handleLoadSecurityCurrencies = useCallback(async () => {
    await runGuaranteesApplicationRequest({
      request: ({ contragentId, applicationId }) =>
        dispatch(fetchGuaranteesSecurityCurrencies({ contragentId, applicationId })).unwrap(),
    });
  }, [dispatch, runGuaranteesApplicationRequest]);

  const handleSaveSecret = async (
    values: TGuaranteesRootSectionFormValues,
    { hasPledgorIdentifyCodeChanged }: TGuaranteesSecretSectionSaveContext = {
      hasPledgorIdentifyCodeChanged: false,
    }
  ) => {
    const firstError = getGuaranteesSecretValidationError(values);

    if (firstError) {
      setModalAlert({ text: firstError });

      return false;
    }

    const result = await runGuaranteesApplicationRequest({
      request: (params) =>
        dispatch(
          patchGuaranteesApplicationSecret({
            ...params,
            payload: buildGuaranteesSecretSectionPayload(values),
          })
        ).unwrap(),
    });

    if (!result) {
      return false;
    }

    const nextVersion = (result as TGuaranteesApplication).version ?? currentApplicationVersion;
    const securityCurrenciesResult = await runGuaranteesApplicationRequest({
      request: ({ contragentId, applicationId }) =>
        dispatch(
          putGuaranteesSecurityCurrencies({
            contragentId,
            applicationId,
            version: nextVersion,
            payload: buildGuaranteesSecurityCurrenciesPayload(values),
          })
        ).unwrap(),
    });

    if (securityCurrenciesResult) {
      setIsSecretSavedForCurrentApplication(true);
      if (hasPledgorIdentifyCodeChanged) {
        try {
          await onSecretSectionSaveSuccess?.();
        } catch (error) {
          showGuaranteesRequestError(error);
        }
      }
    }

    return !!securityCurrenciesResult;
  };

  const handleLoadPrincipal = useCallback(async () => {
    await runGuaranteesApplicationRequest({
      request: ({ contragentId, applicationId }) =>
        dispatch(fetchGuaranteesPrincipal({ contragentId, applicationId })).unwrap(),
    });
  }, [dispatch, runGuaranteesApplicationRequest]);

  const handleSavePrincipal = async (payload: TPatchGuaranteesPrincipalPayload) => {
    const result = await runGuaranteesApplicationRequest({
      request: (params) => dispatch(patchGuaranteesPrincipal({ ...params, payload })).unwrap(),
    });

    return !!result;
  };

  const handleLoadBeneficiary = useCallback(async () => {
    await runGuaranteesApplicationRequest({
      request: ({ contragentId, applicationId }) =>
        dispatch(fetchGuaranteesBeneficiary({ contragentId, applicationId })).unwrap(),
    });
  }, [dispatch, runGuaranteesApplicationRequest]);

  const handleSaveBeneficiary = async (payload: TPatchGuaranteesBeneficiaryPayload) => {
    const result = await runGuaranteesApplicationRequest({
      request: (params) => dispatch(patchGuaranteesBeneficiary({ ...params, payload })).unwrap(),
    });

    return !!result;
  };

  const handleLoadBaseRelations = useCallback(async () => {
    await runGuaranteesApplicationRequest({
      request: ({ contragentId, applicationId }) =>
        dispatch(fetchGuaranteesBaseRelations({ contragentId, applicationId })).unwrap(),
    });
  }, [dispatch, runGuaranteesApplicationRequest]);

  const handleSaveBaseRelations = async (payload: TPatchGuaranteesBaseRelationsPayload) => {
    const result = await runGuaranteesApplicationRequest({
      request: (params) => dispatch(patchGuaranteesBaseRelations({ ...params, payload })).unwrap(),
    });

    return !!result;
  };

  const handleLoadGuaranteeForm = useCallback(async () => {
    await runGuaranteesApplicationRequest({
      request: ({ contragentId, applicationId }) =>
        dispatch(fetchGuaranteesForm({ contragentId, applicationId })).unwrap(),
    });
  }, [dispatch, runGuaranteesApplicationRequest]);

  const handleSaveGuaranteeForm = async (payload: TPatchGuaranteesFormPayload) => {
    const result = await runGuaranteesApplicationRequest({
      request: (params) => dispatch(patchGuaranteesForm({ ...params, payload })).unwrap(),
    });

    return !!result;
  };

  const handleSearchTender = async (tenderPublicId: string) => {
    await runGuaranteesApplicationRequest({
      request: () => dispatch(fetchGuaranteesProzorroTenderById({ tenderPublicId })).unwrap(),
    });
  };

  const handleSelectTender = async (selectedLotIds: string[]) => {
    const tenderPublicId = String(prozorroTender?.tenderPublicId ?? '').trim();

    if (!tenderPublicId) {
      setModalAlert({ text: guaranteesClientText.tenderNotFound });

      return false;
    }

    const result = await runGuaranteesApplicationRequest({
      request: (params) =>
        dispatch(
          patchGuaranteesApplicationTender({
            ...params,
            tenderPublicId,
            selectedLotIds,
          })
        ).unwrap(),
    });

    return isGuaranteesApplicationSelectionPatchSuccessful(result);
  };

  const handleMainDealSelect = useCallback(
    async (option: TGuaranteesContractOption): Promise<boolean> => {
      const mainDealId = option.mainDealId.trim();

      if (!mainDealId) {
        setModalAlert({ text: guaranteesClientText.contractMissingMainDealId });

        return false;
      }

      const result = await runGuaranteesApplicationRequest({
        skipNewApplication: true,
        request: (params) =>
          dispatch(
            patchGuaranteesApplicationMainDeal({
              ...params,
              mainDealId,
            })
          ).unwrap(),
      });

      return isGuaranteesApplicationSelectionPatchSuccessful(result);
    },
    [dispatch, runGuaranteesApplicationRequest, setModalAlert]
  );

  return {
    handleCheckApplicationRequest,
    handleLoadAccounts,
    handleLoadPrincipalDebitAccounts,
    handleLoadSecurityCurrencies,
    handleLoadBaseRelations,
    handleLoadBeneficiary,
    handleLoadGuaranteeForm,
    handleLoadPrincipal,
    handleMainDealSelect,
    handleRootFormSubmit,
    handleSaveBaseRelations,
    handleSaveBeneficiary,
    handleSaveGuaranteeForm,
    handleSavePrincipal,
    handleSaveRootSection,
    handleSaveSecret,
    handleSearchTender,
    handleSelectTender,
  };
};
